import type { ContentCardItem, ContentCardVariant, ContentCardsSettings } from './types';
import { BANNER_PANEL_COLORS, DEFAULT_CONTENT_CARDS_SETTINGS } from './types';

export interface ContentCardTemplatePreset {
  variant: ContentCardVariant;
  label: string;
  description: string;
  settings: ContentCardsSettings;
  sampleCards: ContentCardItem[];
}

export const CONTENT_CARD_TEMPLATES: ContentCardTemplatePreset[] = [
  {
    variant: 'minimal',
    label: 'Minimal',
    description: 'İnce çerçeve, yumuşak gölge ve sade tipografi',
    settings: {
      ...DEFAULT_CONTENT_CARDS_SETTINGS,
      variant: 'minimal',
      layoutTemplate: 'grid',
      cardRadiusPx: 16,
      gapPx: 24,
      showBullets: true
    },
    sampleCards: [
      {
        title: 'Kaliteli Hizmet',
        description: 'Müşterilerimize en iyi deneyimi sunmak için çalışıyoruz.',
        bulletItems: ['Hızlı destek', 'Güvenilir çözüm']
      }
    ]
  },
  {
    variant: 'feature-split',
    label: 'Özellik + Görsel',
    description: 'Büyük başlık, yeşil tik listesi ve sağa/sola alınabilir görsel',
    settings: {
      ...DEFAULT_CONTENT_CARDS_SETTINGS,
      variant: 'feature-split',
      layoutTemplate: 'grid',
      columnsDesktop: 1,
      columnsTablet: 1,
      columnsMobile: 1,
      cardRadiusPx: 20,
      gapPx: 28,
      showBullets: true,
      titleAlign: 'left',
      cardTitleStyles: {
        fontFamily: 'Inter, sans-serif',
        fontSize: 28,
        color: '#1e3a8a',
        bold: true,
        italic: false
      }
    },
    sampleCards: [
      {
        title: 'İşletmenizi Dijital Dönüşüme Hazırlayın',
        imageSide: 'right',
        imageUrl: '',
        bulletItems: [
          'Kapsamlı finansal yönetim',
          'Otomatik hesaplama ve raporlama',
          'Çoklu para birimi desteği',
          'Gerçek zamanlı veri senkronizasyonu',
          'Güvenli veri saklama',
          'Kolay kullanım arayüzü'
        ]
      }
    ]
  },
  {
    variant: 'pricing-trio',
    label: "3'lü Fiyat Paketi",
    description: 'Ürün paketleri için 3 kart; ortadaki paket vurgulu',
    settings: {
      ...DEFAULT_CONTENT_CARDS_SETTINGS,
      variant: 'pricing-trio',
      layoutTemplate: 'promo-trio',
      columnsDesktop: 3,
      columnsTablet: 3,
      columnsMobile: 1,
      cardRadiusPx: 16,
      gapPx: 20,
      showBullets: true,
      titleAlign: 'center',
      cardTitleStyles: {
        fontFamily: 'Inter, sans-serif',
        fontSize: 22,
        color: '#1e3a8a',
        bold: true,
        italic: false
      }
    },
    sampleCards: [
      {
        title: 'Temel',
        description: 'Küçük işletmeler için başlangıç paketi',
        price: '₺340',
        priceSuffix: '/ Aylık',
        badgeColor: '#1e3a8a',
        bulletItems: ['Temel raporlama', 'Tek kullanıcı', 'E-posta destek']
      },
      {
        title: 'Profesyonel',
        description: 'Büyüyen ekipler için önerilen paket',
        price: '₺690',
        priceSuffix: '/ Aylık',
        badge: 'En Popüler',
        featured: true,
        badgeColor: '#1e3a8a',
        bulletItems: [
          'Gelişmiş raporlama',
          '5 kullanıcı',
          'Stok yönetimi',
          'Öncelikli destek'
        ]
      },
      {
        title: 'Kurumsal',
        description: 'Kurumlara özel çözüm',
        price: 'ÖZEL',
        priceSuffix: '',
        badgeColor: '#1e3a8a',
        bulletItems: ['Sınırsız kullanıcı', 'Özel entegrasyon', 'Dedicated destek']
      }
    ]
  },
  {
    variant: 'pricing-scroll',
    label: 'Scroll Fiyat Kartı',
    description: 'İki fiyat kartı; kaydırılabilir özellik listesi ve CTA',
    settings: {
      ...DEFAULT_CONTENT_CARDS_SETTINGS,
      variant: 'pricing-scroll',
      layoutTemplate: 'grid',
      columnsDesktop: 2,
      columnsTablet: 2,
      columnsMobile: 1,
      cardRadiusPx: 16,
      gapPx: 24,
      showBullets: true,
      titleAlign: 'center',
      cardTitleStyles: {
        fontFamily: 'Inter, sans-serif',
        fontSize: 18,
        color: '#1e3a8a',
        bold: true,
        italic: false
      }
    },
    sampleCards: [
      {
        title: 'Yıllık Paket',
        subtitle: 'YILLIK PAKET',
        badge: 'En Popüler Seçenek',
        featured: true,
        price: '50 TL',
        priceSuffix: '/ Kullanıcı / Ay',
        description: 'Yıllık planda avantajlı fiyat.',
        badgeColor: '#1e3a8a',
        ctaLabel: 'Ücretsiz Deneyin',
        bulletItems: [
          'Görev Atama ve Takibi',
          'Takım İşbirliği',
          'Dosya Paylaşımı',
          'Bildirimler',
          'Raporlama',
          'Takvim Entegrasyonu'
        ]
      },
      {
        title: 'Aylık Paket',
        subtitle: 'AYLIK PAKET',
        price: '70 TL',
        priceSuffix: '/ Kullanıcı / Ay',
        description: 'Esnek ödeme seçeneği.',
        badgeColor: '#1e3a8a',
        ctaLabel: 'Ücretsiz Deneyin',
        bulletItems: [
          'Görev Atama ve Takibi',
          'Takım İşbirliği',
          'Dosya Paylaşımı',
          'Bildirimler',
          'Raporlama',
          'Takvim Entegrasyonu'
        ]
      }
    ]
  },
  {
    variant: 'banner',
    label: 'Kampanya Banner',
    description: 'Sol ürün görseli + sağ renkli panel — sade ve şık vitrin kartı',
    settings: {
      ...DEFAULT_CONTENT_CARDS_SETTINGS,
      variant: 'banner',
      layoutTemplate: 'promo-trio',
      cardRadiusPx: 18,
      gapPx: 16,
      showBullets: false,
      showDate: true,
      imageAspectRatio: '1/1',
      titleAlign: 'left',
      cardTitleStyles: {
        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
        fontSize: 20,
        color: '#ffffff',
        bold: true,
        italic: false,
      }
    },
    sampleCards: [
      {
        title: "%15'e varan indirim",
        description: 'Ürünleri Keşfet',
        date: '10 Temmuz - 31 Ağustos',
        panelColor: BANNER_PANEL_COLORS[0],
        imageUrl: '',
        linkUrl: ''
      },
      {
        title: 'Yeni Sezon',
        description: 'Alışverişe Başla',
        date: 'Bu Hafta',
        panelColor: BANNER_PANEL_COLORS[1],
        imageUrl: '',
        linkUrl: ''
      },
      {
        title: 'Özel Fırsatlar',
        description: 'Hemen İncele',
        date: 'Sınırlı Süre',
        panelColor: BANNER_PANEL_COLORS[2],
        imageUrl: '',
        linkUrl: ''
      }
    ]
  },
  {
    variant: 'emoji',
    label: 'Emoji Kart',
    description: 'Renkli daire ikon, ortalı başlık ve kısa açıklama',
    settings: {
      ...DEFAULT_CONTENT_CARDS_SETTINGS,
      variant: 'emoji',
      layoutTemplate: 'grid',
      columnsDesktop: 3,
      columnsTablet: 2,
      columnsMobile: 1,
      cardRadiusPx: 16,
      gapPx: 24,
      showBullets: false,
      titleAlign: 'center',
      gridAlign: 'center',
      cardTitleStyles: {
        fontFamily: 'Inter, sans-serif',
        fontSize: 18,
        color: '#0f172a',
        bold: true,
        italic: false
      }
    },
    sampleCards: [
      {
        useIcon: true,
        iconKey: 'monitor',
        badgeColor: '#2563eb',
        title: 'Profesyonel E-Ticaret Sitesi',
        description:
          'Modern ve responsive tasarım ile müşterilerinizi etkileyecek profesyonel e-ticaret sitenizi dakikalar içinde kurun.'
      },
      {
        useIcon: true,
        iconKey: 'tablet',
        badgeColor: '#16a34a',
        title: 'Mobil Uyumlu Tasarım',
        description: 'Tüm cihazlarda mükemmel görünüm. Müşterileriniz her yerden kolayca alışveriş yapsın.'
      },
      {
        useIcon: true,
        iconKey: 'settings',
        badgeColor: '#f97316',
        title: 'Kolay Yönetim Paneli',
        description: 'Ürün ekleme, stok takibi, sipariş yönetimi — hepsi tek panelden, hiç kod bilgisi gerektirmeden.'
      },
      {
        useIcon: true,
        iconKey: 'credit-card',
        badgeColor: '#7c3aed',
        title: 'Güvenli Ödeme Sistemi',
        description: 'Tüm ödeme yöntemlerini destekleyen, SSL korumalı güvenli ödeme altyapısı.'
      },
      {
        useIcon: true,
        iconKey: 'trending-up',
        badgeColor: '#dc2626',
        title: 'Detaylı Raporlama',
        description: 'Satış, ziyaretçi ve ürün performanslarını anlık takip edin, veriye dayalı kararlar alın.'
      },
      {
        useIcon: true,
        iconKey: 'truck',
        badgeColor: '#0ea5e9',
        title: 'Kargo Entegrasyonu',
        description: 'Popüler kargo firmalarıyla entegre çalışın, otomatik kargo takip numarası oluşturun.'
      }
    ]
  }
];

export const getContentCardTemplate = (variant: ContentCardVariant): ContentCardTemplatePreset => {
  const found = CONTENT_CARD_TEMPLATES.find((item) => item.variant === variant);
  return found || CONTENT_CARD_TEMPLATES[0];
};

export const getContentCardTemplateLabel = (variant: ContentCardVariant): string =>
  getContentCardTemplate(variant).label;
